import React, { Fragment, useState, useMemo, useId } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption, Transition } from '@headlessui/react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Tooltip } from './Tooltip';

export interface AutocompleteOption {
  id: string;
  label: string; // Text to display in the list (e.g. "Name - Roast Type (Stock)")
  text: string;  // Text to display in the input/selected state (e.g. "Name")
  value?: string; // Optional value for backwards compatibility or specific needs
}

export type AutocompleteSelectSize = 'sm' | 'md';

export interface AutocompleteSelectProps {
  label?: string;
  value: string; // The current ID or text value
  onChange: (value: string, option?: AutocompleteOption) => void;
  onCreateOption?: (text: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
  dataTestId?: string;
  allowCustom?: boolean;
  displayValueOverride?: string;
  // Size token controlling padding, font-size, and corner radius. 'md'
  // (default) matches stand-alone form fields. 'sm' matches the dense form
  // rows where this autocomplete sits next to a regular `p-2.5 text-xs`
  // <input>, so heights align without per-call className overrides.
  size?: AutocompleteSelectSize;
  inputClassName?: string;
  wrapperClassName?: string;
  optionClassName?: string;
  optionsClassName?: string;
}

export const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  label,
  value,
  onChange,
  onCreateOption,
  options,
  placeholder,
  error,
  className,
  disabled,
  dataTestId,
  allowCustom = true,
  displayValueOverride,
  size = 'md',
  inputClassName,
  wrapperClassName,
  optionClassName,
  optionsClassName,
}) => {
  const sizeWrapperClass = size === 'sm' ? 'rounded-md' : 'rounded-lg';
  // sm padding matches a sibling `<input p-2.5 sm:text-xs border>` so the
  // autocomplete lines up flush in dense form rows.
  const sizeInputClass = size === 'sm' ? 'py-2.5 pl-2.5 pr-9 text-xs' : 'py-2.5 pl-3.5 pr-10 text-sm';
  const sizeButtonPaddingClass = size === 'sm' ? 'pr-2' : 'pr-2.5';
  const [query, setQuery] = useState('');
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const fallbackName = useId();

  const filteredOptions = useMemo(() => {
    return query === ''
      ? options
      : options.filter((option) =>
          option.text.toLowerCase().includes(query.toLowerCase()) ||
          option.label.toLowerCase().includes(query.toLowerCase())
        );
  }, [query, options]);

  const selectedOption = useMemo(() => {
    return options.find(o => o.id === value || o.value === value || o.text === value) || null;
  }, [value, options]);

  const handleSelect = (selected: AutocompleteOption | string | null) => {
    if (!selected) {
      onChange('');
      return;
    }

    if (typeof selected === 'string') {
      onChange(selected);
      setQuery('');
      
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setTimeout(() => {
        onCreateOption?.(selected);
      }, 0);
    } else {
      onChange(selected.id, selected);
      setQuery('');
    }
  };

  return (
    <div className={cn("w-full", className)}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>}
      <Tooltip content={
        displayValueOverride || 
        (selectedOption ? selectedOption.text : (typeof value === 'string' ? value : ''))
      }>
        <Combobox<AutocompleteOption | string>
          value={selectedOption || value}
          onChange={handleSelect}
          nullable
          disabled={disabled}
          immediate
        >
        <div className="relative">
          <div
            className={cn(
              "relative w-full cursor-default overflow-hidden bg-white text-left border border-gray-300 transition-all duration-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 sm:text-sm shadow-sm hover:border-gray-400",
              sizeWrapperClass,
              !!error && !disabled && "border-red-300 focus-within:border-red-500 focus-within:ring-red-500/20 hover:border-red-400",
              disabled && "bg-gray-50 cursor-not-allowed border-gray-200 hover:border-gray-200",
              wrapperClassName
            )}
            onMouseDown={(event) => {
              if (disabled) return;
              const target = event.target as Node | null;
              if (target && buttonRef.current?.contains(target)) return;
              event.preventDefault();
              inputRef.current?.focus();
              if (buttonRef.current?.getAttribute('aria-expanded') === 'false') {
                buttonRef.current.click();
              }
            }}
          >
            <ComboboxInput
              ref={inputRef}
              className={cn(
                "w-full border-none leading-5 text-gray-900 focus:ring-0 placeholder-gray-400 bg-transparent",
                sizeInputClass,
                inputClassName
              )}
              displayValue={(val: AutocompleteOption | string | null) => {
                if (displayValueOverride) return displayValueOverride;
                if (!val) return '';
                if (typeof val === 'string') return val;
                return val.text;
              }}
              onChange={(event) => setQuery(event.target.value)}
              onClick={() => {
                if (buttonRef.current && buttonRef.current.getAttribute('aria-expanded') === 'false') {
                  buttonRef.current.click();
                }
              }}
              onFocus={(event) => {
                event.target.select();
                if (query === '' && options.length > 0) {
                  setQuery('');
                }
              }}
              placeholder={placeholder}
              data-test-id={dataTestId || `autocomplete-input-${label?.toLowerCase().replace(/\s+/g, '-') || 'select'}`}
              disabled={disabled}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              name={`autocomplete-${label?.toLowerCase().replace(/\s+/g, '-') || dataTestId || fallbackName}`}
            />
            <ComboboxButton
              ref={buttonRef}
              className={cn(
                "absolute inset-y-0 right-0 flex items-center",
                sizeButtonPaddingClass,
                disabled && "pointer-events-none opacity-40"
              )}
              data-test-id={`autocomplete-button-${label?.toLowerCase().replace(/\s+/g, '-') || 'select'}`}
            >
              <ChevronsUpDown
                className="h-4.5 w-4.5 text-gray-400 hover:text-gray-500 transition-colors"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <ComboboxOptions className={cn("absolute z-[100] mt-1.5 max-h-64 w-full overflow-auto rounded-xl bg-white py-1.5 text-base shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border border-gray-100", optionsClassName)}>
              {query.length > 0 && allowCustom && !options.some(o => o.text.toLowerCase() === query.toLowerCase() || o.label.toLowerCase() === query.toLowerCase()) && (
                <ComboboxOption
                  value={query}
                  className={({ active }) =>
                    cn(
                      'relative cursor-pointer select-none py-2.5 px-4 transition-colors',
                      active ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                    )
                  }
                >
                  <span className="flex items-center gap-2">
                    <span className="text-primary-600 font-medium">+</span>
                    Create "{query}"
                  </span>
                </ComboboxOption>
              )}
              {filteredOptions.length === 0 && query !== '' && !allowCustom ? (
                <div className="relative cursor-default select-none py-3 px-4 text-gray-500 italic text-center">
                  No matches found.
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <ComboboxOption
                    key={option.id}
                    value={option}
                    className={({ active, selected }) =>
                      cn(
                        'relative cursor-pointer select-none py-2.5 pl-10 pr-4 transition-all duration-150',
                        active ? 'bg-primary-50 text-primary-900' : 'text-gray-900',
                        selected && !active && 'bg-gray-50'
                      )
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={cn(
                            'block',
                            optionClassName ? optionClassName : 'truncate',
                            selected ? 'font-semibold text-primary-700' : 'font-normal'
                          )}
                        >
                          {option.label}
                        </span>
                        {selected ? (
                          <span
                            className={cn(
                              'absolute inset-y-0 left-0 flex items-center pl-3.5',
                              active ? 'text-primary-600' : 'text-primary-500'
                            )}
                          >
                            <Check className="h-4.5 w-4.5" aria-hidden="true" strokeWidth={3} />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
        </Combobox>
      </Tooltip>
      {error && <p className="mt-1.5 text-xs text-red-600 font-medium ml-1">{error}</p>}
    </div>
  );
};
